/**
 * Credit Control Module — Iter-101
 *
 * Financial Brain UI. 5 tabs:
 *   1. Overview           — KPI tiles + DSO trend
 *   2. Customer Exposure  — sortable risk-ranked table
 *   3. Aging Report       — re-uses /reports/ar-aging
 *   4. Cash Forecast      — re-uses /reports/cash-forecast
 *   5. Collection Workspace + Customer Drill (drawer)
 */
import React, { useEffect, useMemo, useState } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  TrendingUp, Wallet, AlertCircle, Users, ShieldAlert,
  Activity, DollarSign, ArrowDownRight, ArrowUpRight,
  Phone, Mail, MessageSquare, AlertTriangle, ClipboardList,
  CheckCircle2, Loader2, Settings2, X, ChevronRight,
} from "lucide-react";

const SAR = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "SAR", maximumFractionDigits: 0,
}).format(Number(n || 0));

const RISK_BADGE = {
  low:      "bg-emerald-100 text-emerald-800 border-emerald-300",
  medium:   "bg-amber-100 text-amber-800 border-amber-300",
  high:     "bg-orange-100 text-orange-800 border-orange-300",
  critical: "bg-rose-100 text-rose-800 border-rose-300",
};

const TABS = [
  { id: "overview",   label: "نظرة عامة",         icon: Activity     },
  { id: "exposure",   label: "تعرض العملاء",      icon: Wallet       },
  { id: "aging",      label: "تقادم الذمم",       icon: TrendingUp   },
  { id: "forecast",   label: "تدفق نقدي",         icon: DollarSign   },
  { id: "workspace",  label: "مساحة التحصيل",     icon: ClipboardList},
];

// ─── KPI Tile ────────────────────────────────────────────────────

const KPI = ({ label, value, icon: Icon, color = "slate", subtle, tone = "" }) => (
  <div data-testid={`cc-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
       className={`relative rounded-xl border border-${color}-200 bg-white p-4 shadow-sm`}>
    <div className="flex items-center justify-between">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-${color}-100 text-${color}-700`}>
        <Icon className="h-5 w-5" />
      </div>
      {tone && (
        <span className={`text-xs font-medium ${tone}`}>{tone.includes("emerald") ? "↑" : "↓"}</span>
      )}
    </div>
    <div className="mt-3 text-xs font-medium text-slate-500">{label}</div>
    <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
    {subtle && <div className="mt-0.5 text-xs text-slate-500">{subtle}</div>}
  </div>
);

// ─── Overview Tab ────────────────────────────────────────────────

const OverviewTab = ({ summary, dso }) => {
  const maxDso = useMemo(
    () => Math.max(1, ...(dso?.points || []).map(p => p.dso || 0)),
    [dso],
  );
  if (!summary) return <div className="p-8 text-center text-slate-400">جارٍ التحميل…</div>;
  return (
    <div className="space-y-6">
      {/* KPI grid */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <KPI label="إجمالي التعرض"   value={SAR(summary.total_exposure)}
              icon={Wallet}   color="indigo" subtle={`${summary.overdue_invoices} فاتورة متأخرة`} />
        <KPI label="المتأخرات"        value={SAR(summary.total_overdue)}
              icon={AlertCircle} color="rose" subtle={`${summary.overdue_invoices} فاتورة متأخرة`} />
        <KPI label="DSO الشركة"       value={`${summary.company_dso_days} يوم`}
              icon={TrendingUp} color="violet" subtle="آخر 90 يوم" />
        <KPI label="عملاء عالي الخطر" value={summary.customers_at_risk}
              icon={ShieldAlert} color="amber"
              subtle={`من ${summary.active_customers} عميل نشط`} />
        <KPI label="حد الائتمان الإجمالي" value={SAR(summary.total_credit_limit)}
              icon={DollarSign} color="emerald" />
        <KPI label="الاستخدام"        value={`${summary.credit_utilization}%`}
              icon={Activity} color="sky"
              subtle="مفتوح / حد ائتماني" />
      </div>

      {/* DSO trend mini-chart */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">اتجاه DSO الشهري</h3>
          <span className="text-xs text-slate-500">آخر {dso?.points?.length || 0} شهراً</span>
        </div>
        <div className="flex items-end gap-2 h-40" data-testid="cc-dso-chart">
          {(dso?.points || []).map((p) => {
            const h = (p.dso / maxDso) * 100;
            const tone = p.dso > 60 ? "bg-rose-400" : p.dso > 45 ? "bg-amber-400" : "bg-emerald-400";
            return (
              <div key={p.month} className="flex-1 flex flex-col items-center gap-1">
                <div className="flex-1 w-full flex items-end">
                  <div className={`w-full ${tone} rounded-t transition-all`}
                        style={{ height: `${h}%` }}
                        title={`${p.month}: ${p.dso}d`} />
                </div>
                <span className="text-[10px] font-mono text-slate-500">{p.month.slice(5)}</span>
                <span className="text-[10px] font-bold text-slate-700">{p.dso}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Customer Exposure Tab ───────────────────────────────────────

const ExposureTable = ({ rows, onPick }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
    <table data-testid="cc-exposure-table" className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          <th className="px-4 py-3 text-right font-semibold">العميل</th>
          <th className="px-4 py-3 text-right font-semibold">المفتوح</th>
          <th className="px-4 py-3 text-right font-semibold">المتأخر</th>
          <th className="px-4 py-3 text-right font-semibold">أقدم تأخير</th>
          <th className="px-4 py-3 text-right font-semibold">DSO</th>
          <th className="px-4 py-3 text-right font-semibold">الاستخدام</th>
          <th className="px-4 py-3 text-right font-semibold">المخاطر</th>
          <th className="px-4 py-3 text-center font-semibold">إجراء</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.length === 0 && (
          <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">
            لا يوجد عملاء حالياً
          </td></tr>
        )}
        {rows.map((r) => (
          <tr key={r.customer_id} data-testid={`cc-row-${r.customer_id}`}
              className="hover:bg-violet-50/50 cursor-pointer"
              onClick={() => onPick(r.customer_id)}>
            <td className="px-4 py-3">
              <div className="font-medium text-slate-900">{r.customer_name}</div>
              <div className="text-xs text-slate-500 font-mono">{r.customer_number}</div>
            </td>
            <td className="px-4 py-3 font-mono text-slate-700">{SAR(r.total_open)}</td>
            <td className="px-4 py-3 font-mono">
              <span className={r.overdue_amount > 0 ? "text-rose-700 font-semibold" : "text-slate-400"}>
                {SAR(r.overdue_amount)}
              </span>
            </td>
            <td className="px-4 py-3 text-center">
              {r.oldest_overdue_days > 0
                ? <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs text-rose-700">{r.oldest_overdue_days}ي</span>
                : <span className="text-slate-300">—</span>}
            </td>
            <td className="px-4 py-3 text-center font-mono text-slate-600">{r.dso_days}</td>
            <td className="px-4 py-3 text-center">
              {r.credit_limit > 0
                ? <span className="font-mono text-slate-700">{r.utilization_pct}%</span>
                : <span className="text-xs text-slate-400">—</span>}
            </td>
            <td className="px-4 py-3">
              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-bold ${RISK_BADGE[r.risk_bucket]}`}>
                {r.risk_score}
                <span className="text-[10px] uppercase">{r.risk_bucket}</span>
              </span>
            </td>
            <td className="px-4 py-3 text-center">
              <ChevronRight className="inline h-4 w-4 text-slate-300" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Aging Tab ───────────────────────────────────────────────────

const AgingTab = ({ data }) => {
  if (!data) return <div className="p-8 text-center text-slate-400">جارٍ التحميل…</div>;
  const t = data.totals || {};
  const buckets = [
    { key: "current", label: "حالية (0-30)", color: "emerald" },
    { key: "31-60",   label: "31-60 يوم",    color: "amber"   },
    { key: "61-90",   label: "61-90 يوم",    color: "orange"  },
    { key: "90+",     label: "+90 يوم",      color: "rose"    },
  ];
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-4">
        {buckets.map(b => (
          <div key={b.key} className={`rounded-xl border border-${b.color}-200 bg-white p-4 shadow-sm`}>
            <div className="text-xs font-medium text-slate-500">{b.label}</div>
            <div className={`mt-2 text-2xl font-bold text-${b.color}-700`}>{SAR(t[b.key])}</div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-right">العميل</th>
              <th className="px-4 py-3 text-right">حالية</th>
              <th className="px-4 py-3 text-right">31-60</th>
              <th className="px-4 py-3 text-right">61-90</th>
              <th className="px-4 py-3 text-right">+90</th>
              <th className="px-4 py-3 text-right">الإجمالي</th>
              <th className="px-4 py-3 text-center">أقدم تأخير</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data.buckets || []).map(r => (
              <tr key={r.customer_id} className="hover:bg-violet-50/50">
                <td className="px-4 py-3 font-medium text-slate-900">{r.customer_name}</td>
                <td className="px-4 py-3 font-mono text-emerald-700">{SAR(r.current)}</td>
                <td className="px-4 py-3 font-mono text-amber-700">{SAR(r["31-60"])}</td>
                <td className="px-4 py-3 font-mono text-orange-700">{SAR(r["61-90"])}</td>
                <td className="px-4 py-3 font-mono text-rose-700">{SAR(r["90+"])}</td>
                <td className="px-4 py-3 font-mono font-bold text-slate-900">{SAR(r.total_outstanding)}</td>
                <td className="px-4 py-3 text-center text-xs text-slate-600">
                  {r.oldest_invoice_days > 0 ? `${r.oldest_invoice_days}ي` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Cash Forecast Tab ───────────────────────────────────────────

const ForecastTab = ({ data }) => {
  if (!data) return <div className="p-8 text-center text-slate-400">جارٍ التحميل…</div>;
  const maxFlow = Math.max(1, ...(data.weeks || []).flatMap(w => [w.cash_in, w.cash_out]));
  const t = data.totals || {};
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-3">
        <KPI label="إجمالي المتوقع داخل"  value={SAR(t.total_cash_in)}  icon={ArrowDownRight} color="emerald" />
        <KPI label="إجمالي المتوقع خارج" value={SAR(t.total_cash_out)} icon={ArrowUpRight}   color="rose"    />
        <KPI label="صافي المركز"          value={SAR(t.net_position)}
              icon={TrendingUp} color={t.net_position >= 0 ? "emerald" : "rose"}
              subtle={`${t.horizon_weeks} أسبوع`} />
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-sm font-bold text-slate-900">تدفق نقدي أسبوعي</h3>
        <div className="flex items-end gap-1.5 h-48 overflow-x-auto pb-2">
          {(data.weeks || []).map(w => (
            <div key={w.week_index} className="min-w-[40px] flex flex-col items-center gap-1">
              <div className="flex-1 w-full flex items-end gap-0.5">
                <div className="w-1/2 bg-emerald-400 rounded-t" title={`In: ${w.cash_in}`}
                      style={{ height: `${(w.cash_in / maxFlow) * 100}%` }} />
                <div className="w-1/2 bg-rose-400 rounded-t" title={`Out: ${w.cash_out}`}
                      style={{ height: `${(w.cash_out / maxFlow) * 100}%` }} />
              </div>
              <span className="text-[10px] font-mono text-slate-500">{w.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Collection Workspace Tab ────────────────────────────────────

const ACTION_META = {
  call_made:      { icon: Phone,           label: "اتصال هاتفي",   color: "blue"    },
  email_sent:     { icon: Mail,            label: "بريد إلكتروني", color: "violet"  },
  sms_sent:       { icon: MessageSquare,   label: "SMS",           color: "indigo"  },
  whatsapp_sent:  { icon: MessageSquare,   label: "واتساب",        color: "emerald" },
  reminder_sent:  { icon: AlertCircle,     label: "تذكير",         color: "amber"   },
  escalated:      { icon: AlertTriangle,   label: "تصعيد",         color: "rose"    },
  promise_to_pay: { icon: CheckCircle2,    label: "وعد بالسداد",   color: "sky"     },
  dispute_logged: { icon: AlertTriangle,   label: "نزاع",          color: "orange"  },
  note:           { icon: ClipboardList,   label: "ملاحظة",        color: "slate"   },
};

const ActionDialog = ({ row, onClose, onSaved }) => {
  const [type, setType] = useState("call_made");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await axiosInstance.post("/credit-control/collection-actions", {
        customer_id: row.customer_id, action_type: type, notes,
      });
      toast.success("تم تسجيل الإجراء");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تسجيل الإجراء");
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="cc-action-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">تسجيل إجراء تحصيل</h3>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-2 text-sm text-slate-500">العميل: {row.customer_name}</div>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">نوع الإجراء</label>
            <select data-testid="cc-action-type"
                     value={type} onChange={(e) => setType(e.target.value)}
                     className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
              {Object.entries(ACTION_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">ملاحظات</label>
            <textarea data-testid="cc-action-notes"
                       value={notes} onChange={(e) => setNotes(e.target.value)}
                       rows={3} maxLength={500}
                       placeholder="تفاصيل المكالمة / الرسالة / النزاع…"
                       className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose}
                   className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
            إلغاء
          </button>
          <button data-testid="cc-action-submit" disabled={saving} onClick={submit}
                   className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            حفظ
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkspaceTab = ({ items, onAction, onPick }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
    <table data-testid="cc-workspace-table" className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          <th className="px-4 py-3 text-right">الأولوية</th>
          <th className="px-4 py-3 text-right">العميل</th>
          <th className="px-4 py-3 text-right">المتأخر</th>
          <th className="px-4 py-3 text-right">المخاطر</th>
          <th className="px-4 py-3 text-right">أقدم تأخير</th>
          <th className="px-4 py-3 text-right">آخر إجراء</th>
          <th className="px-4 py-3 text-center">إجراء</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {items.length === 0 && (
          <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">
            لا يوجد متأخرات تستدعي تحصيلاً
          </td></tr>
        )}
        {items.map((r, idx) => {
          const lastMeta = r.last_action ? ACTION_META[r.last_action] : null;
          return (
            <tr key={r.customer_id} className="hover:bg-violet-50/50">
              <td className="px-4 py-3">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-rose-100 text-xs font-bold text-rose-700">
                  {idx + 1}
                </span>
              </td>
              <td className="px-4 py-3 cursor-pointer" onClick={() => onPick(r.customer_id)}>
                <div className="font-medium text-slate-900">{r.customer_name}</div>
                <div className="text-xs text-slate-500 font-mono">{r.open_invoices} فاتورة مفتوحة</div>
              </td>
              <td className="px-4 py-3 font-mono font-bold text-rose-700">{SAR(r.overdue_amount)}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-bold ${RISK_BADGE[r.risk_bucket]}`}>
                  {r.risk_score}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs text-rose-700">
                  {r.oldest_overdue_days}ي
                </span>
              </td>
              <td className="px-4 py-3 text-xs text-slate-600">
                {lastMeta
                  ? <span className={`inline-flex items-center gap-1 text-${lastMeta.color}-700`}>
                      <lastMeta.icon className="h-3.5 w-3.5" /> {lastMeta.label}
                    </span>
                  : <span className="text-slate-400">لا توجد</span>}
              </td>
              <td className="px-4 py-3 text-center">
                <button data-testid={`cc-action-${r.customer_id}`}
                         onClick={() => onAction(r)}
                         className="rounded-lg border border-violet-300 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-100">
                  تسجيل إجراء
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ─── Customer Drawer ─────────────────────────────────────────────

const CustomerDrawer = ({ customerId, onClose }) => {
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    (async () => {
      setData(null);
      try {
        const r = await axiosInstance.get(`/credit-control/customers/${customerId}`);
        setData(r.data);
        setLimit(String(r.data?.exposure?.credit_limit || ""));
      } catch (e) {
        toast.error("فشل جلب تفاصيل العميل");
        onClose();
      }
    })();
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveLimit = async () => {
    setSavingLimit(true);
    try {
      await axiosInstance.post(
        `/credit-control/customers/${customerId}/credit-limit`,
        { credit_limit: Number(limit || 0), currency: "SAR" },
      );
      toast.success("تم تحديث حد الائتمان");
      const r = await axiosInstance.get(`/credit-control/customers/${customerId}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحديث حد الائتمان");
    } finally { setSavingLimit(false); }
  };

  if (!customerId) return null;
  return (
    <div data-testid="cc-customer-drawer"
          className="fixed inset-y-0 right-0 z-40 w-full max-w-2xl overflow-y-auto border-l border-slate-200 bg-white shadow-2xl"
          dir="rtl">
      <div className="flex items-center justify-between border-b border-slate-200 p-4">
        <h3 className="text-lg font-bold text-slate-900">تفاصيل العميل</h3>
        <button onClick={onClose} className="rounded-full p-1 hover:bg-slate-100">
          <X className="h-4 w-4" />
        </button>
      </div>
      {!data && (
        <div className="p-12 text-center text-slate-400">
          <Loader2 className="inline h-5 w-5 animate-spin" /> جارٍ التحميل…
        </div>
      )}
      {data && (
        <div className="space-y-5 p-5">
          <div>
            <div className="text-2xl font-bold text-slate-900">{data.exposure.customer_name}</div>
            <div className="text-xs font-mono text-slate-500">{data.exposure.customer_number}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <KPI label="المفتوح"      value={SAR(data.exposure.total_open)}     icon={Wallet}     color="indigo" />
            <KPI label="المتأخر"      value={SAR(data.exposure.overdue_amount)} icon={AlertCircle} color="rose"   />
            <KPI label="DSO"          value={`${data.exposure.dso_days} ي`}     icon={TrendingUp} color="violet" />
            <KPI label="متوسط التأخير" value={`${data.exposure.avg_payment_days} ي`} icon={Activity} color="amber" />
          </div>

          {/* Risk factors */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">درجة المخاطر</div>
              <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-bold ${RISK_BADGE[data.exposure.risk_bucket]}`}>
                {data.exposure.risk_score} / 100 · {data.exposure.risk_bucket.toUpperCase()}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              {Object.entries(data.exposure.risk_factors || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="w-32 text-slate-600">{({
                    avg_payment_delay: "تأخير السداد",
                    oldest_overdue:    "أقدم تأخير",
                    utilization:       "الاستخدام",
                    disputes:          "نزاعات",
                  })[k] || k}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div className="h-full bg-violet-500" style={{ width: `${v}%` }} />
                  </div>
                  <span className="font-mono text-slate-700 w-10 text-right">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Credit limit editor */}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <Settings2 className="h-4 w-4 text-violet-600" /> حد الائتمان
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input data-testid="cc-credit-limit-input"
                      dir="ltr" type="number" min={0}
                      value={limit} onChange={(e) => setLimit(e.target.value)}
                      placeholder="0"
                      className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" />
              <button data-testid="cc-credit-limit-save"
                       onClick={saveLimit} disabled={savingLimit}
                       className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
                {savingLimit && <Loader2 className="h-4 w-4 animate-spin" />}
                حفظ
              </button>
            </div>
            <div className="mt-2 text-xs text-slate-500">
              الاستخدام الحالي: <strong>{data.exposure.utilization_pct}%</strong>
            </div>
          </div>

          {/* Open invoices */}
          <div>
            <h4 className="mb-2 text-sm font-bold text-slate-900">
              الفواتير المفتوحة ({data.open_invoices.length})
            </h4>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-right">الرقم</th>
                    <th className="px-3 py-2 text-right">الإجمالي</th>
                    <th className="px-3 py-2 text-right">المتبقي</th>
                    <th className="px-3 py-2 text-right">الاستحقاق</th>
                    <th className="px-3 py-2 text-center">تأخير</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.open_invoices.map(i => (
                    <tr key={i.id}>
                      <td className="px-3 py-2 font-mono text-slate-900">{i.invoice_number}</td>
                      <td className="px-3 py-2 font-mono">{SAR(i.total)}</td>
                      <td className="px-3 py-2 font-mono text-rose-700">{SAR(i.amount_remaining)}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{i.due_date}</td>
                      <td className="px-3 py-2 text-center">
                        {i.days_overdue > 0
                          ? <span className="rounded-full bg-rose-50 px-2 py-0.5 text-rose-700">{i.days_overdue}ي</span>
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────

export default function CreditControl() {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [dso, setDso]         = useState(null);
  const [exposure, setExposure] = useState([]);
  const [aging, setAging]     = useState(null);
  const [forecast, setForecast] = useState(null);
  const [workspace, setWorkspace] = useState([]);
  const [drawerId, setDrawerId] = useState(null);
  const [actionRow, setActionRow] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [s, d] = await Promise.all([
          axiosInstance.get("/credit-control/summary"),
          axiosInstance.get("/credit-control/dso/trend?months=12"),
        ]);
        setSummary(s.data); setDso(d.data);
      } catch { toast.error("فشل تحميل KPIs"); }
    })();
  }, [refreshTick]);

  useEffect(() => {
    if (tab !== "exposure" && tab !== "workspace") return;
    (async () => {
      try {
        if (tab === "exposure") {
          const r = await axiosInstance.get("/credit-control/customers?has_open=true&limit=200");
          setExposure(r.data?.customers || []);
        }
        if (tab === "workspace") {
          const r = await axiosInstance.get("/credit-control/collection-workspace?limit=100");
          setWorkspace(r.data?.items || []);
        }
      } catch { toast.error("فشل تحميل البيانات"); }
    })();
  }, [tab, refreshTick]);

  useEffect(() => {
    if (tab !== "aging") return;
    axiosInstance.get("/reports/ar-aging").then(r => setAging(r.data))
      .catch(() => toast.error("فشل تحميل تقادم الذمم"));
  }, [tab, refreshTick]);

  useEffect(() => {
    if (tab !== "forecast") return;
    axiosInstance.get("/reports/cash-forecast?weeks=12").then(r => setForecast(r.data))
      .catch(() => toast.error("فشل تحميل التدفق النقدي"));
  }, [tab, refreshTick]);

  return (
    <div data-testid="credit-control-page" className="space-y-6 p-6" dir="rtl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          <Wallet className="inline -mt-1 ml-2 h-7 w-7 text-violet-600" />
          Credit Control
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          الدماغ المالي — تعرض العملاء · DSO · مخاطر · توقع نقدي · مساحة تحصيل
        </p>
      </div>

      {/* Tabs */}
      <div data-testid="cc-tabs" className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} data-testid={`cc-tab-${t.id}`}
                     onClick={() => setTab(t.id)}
                     className={`inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-all ${
                       active ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"
                     }`}>
              <Icon className="h-4 w-4" />{t.label}
            </button>
          );
        })}
      </div>

      <div>
        {tab === "overview"  && <OverviewTab summary={summary} dso={dso} />}
        {tab === "exposure"  && <ExposureTable rows={exposure} onPick={setDrawerId} />}
        {tab === "aging"     && <AgingTab data={aging} />}
        {tab === "forecast"  && <ForecastTab data={forecast} />}
        {tab === "workspace" && <WorkspaceTab items={workspace}
                                                onPick={setDrawerId}
                                                onAction={setActionRow} />}
      </div>

      {drawerId && <CustomerDrawer customerId={drawerId} onClose={() => setDrawerId(null)} />}
      {actionRow && (
        <ActionDialog
          row={actionRow}
          onClose={() => setActionRow(null)}
          onSaved={() => { setActionRow(null); setRefreshTick(t => t + 1); }}
        />
      )}
    </div>
  );
}
